import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CreateNewFolderOutlined } from '@mui/icons-material';
import { UserTypeEnum } from 'enums';
import { useCreations } from 'contexts/useCreations';
import { useAuthUser } from 'features/auth/AuthUserContext';
import ResearcherLifeCouponModal from 'components/ResearcherLifeCouponModal';

export const useStyles = makeStyles((theme) => ({
  divider: {
    marginTop: theme.spacing(2),
  },
  input: {
    width: 160,
    marginRight: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(3),
    },
  },
  textRight: {
    textAlign: 'right',
  },
  tooltip: {
    backgroundColor: theme.palette.common.black,
  },
}));

const Props = {
  filters: PropTypes.shape({
    status: PropTypes.string.isRequired,
    order: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onCreateFolderClick: PropTypes.func.isRequired,
};

export default function Filters({ filters, onChange, onCreateFolderClick }) {
  const classes = useStyles();
  const { user, updateUser, userJournalSettings } = useAuthUser();
  const [allCreations] = useCreations();
  const [journalCreationsCount, setJournalCreationsCount] = useState(0);
  const [shouldOpenActivationModal, setShouldOpenActivationModal] = useState(false);
  const [validCoupon, setValidCoupon] = useState(false);
  const getLabel = (value) => {
    switch (value) {
      case 'available':
        return 'Owned by me';
      case 'shared':
        return 'Shared with me';
      case 'deleted':
        return 'Deleted';
      case 'last_modified':
        return 'Last modified';
      case 'a_to_z':
        return 'A to Z';
      default:
        return 'Unknown';
    }
  };

  function getAvailableCreationsLabel() {
    if (user.tp_user === UserTypeEnum.STUDENT) {
      return allCreations.length <= 2 ? `${2 - allCreations.length}` : '0';
    }

    if (user.tp_user === UserTypeEnum.JOURNAL) {
      const available = userJournalSettings.creationsAvailable - journalCreationsCount;
      return available > 0 ? available : '0';
    }

    return 'unlimited';
  }

  useEffect(() => {
    if (user.tp_user === UserTypeEnum.JOURNAL && userJournalSettings?.createdAt) {
      const journalStartDate = new Date(userJournalSettings.createdAt);

      const journalCreations = allCreations.filter((creation) => {
        const creationDate = new Date(creation.created_at);
        return creationDate >= journalStartDate;
      });

      setJournalCreationsCount(journalCreations.length);
    }
  }, [allCreations, user.tp_user, userJournalSettings]);

  useEffect(() => {
    const fromJournal = sessionStorage.getItem('fromJournalRegistration');

    if (fromJournal === 'true') {
      setShouldOpenActivationModal(true);
      sessionStorage.removeItem('fromJournalRegistration');
    }
  }, []);

  useEffect(() => {
    if (validCoupon) {
      updateUser();
    }
  }, [validCoupon]);

  return (
    <>
      <Grid container={true} spacing={2} alignItems="center">
        <Grid item={true} xs={10} style={{ display: 'flex', alignItems: 'center' }}>
          <FormControl variant="outlined" size="small" className={classes.input}>
            <InputLabel
              id="recent-creations-label"
              style={{ background: '#fafafa', paddingInline: '6px', marginLeft: '-3px' }}
            >
              Recent creations
            </InputLabel>

            <Select
              labelId="recent-creations-label"
              id="recent-creations"
              value={filters.status}
              onChange={({ target: { value } }) => onChange({ ...filters, status: value })}
              renderValue={(selected) => (
                <div
                  role="textbox"
                  aria-readonly="true"
                  tabIndex={-1}
                  aria-label="Selected filter: Recent creations"
                >
                  {getLabel(selected)}
                </div>
              )}
              inputProps={{
                'aria-controls': 'recent-creations-menu',
              }}
              MenuProps={{
                id: 'recent-creations-menu',
              }}
            >
              <MenuItem value="available" role="option">
                Owned by me
              </MenuItem>
              <MenuItem value="shared" role="option">
                Shared with me
              </MenuItem>
              <MenuItem value="deleted" role="option">
                Deleted
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.input}>
            <InputLabel
              id="sort-by-label"
              style={{ background: '#fafafa', paddingInline: '6px', marginLeft: '-3px' }}
            >
              Sort by
            </InputLabel>

            <Select
              labelId="sort-by-label"
              id="sort-by"
              value={filters.order}
              onChange={({ target: { value } }) => onChange({ ...filters, order: value })}
              renderValue={(selected) => (
                <div
                  role="textbox"
                  aria-readonly="true"
                  tabIndex={-1}
                  aria-label="Selected sort option: Sort by"
                >
                  {getLabel(selected)}
                </div>
              )}
              inputProps={{
                'aria-controls': 'sort-by-menu',
              }}
              MenuProps={{
                id: 'sort-by-menu',
              }}
              variant="outlined"
              size="small"
              className={classes.input}
            >
              <MenuItem value="last_modified" role="option">
                Last modified
              </MenuItem>
              <MenuItem value="a_to_z" role="option">
                A to Z
              </MenuItem>
            </Select>
          </FormControl>

          {/* <Box mr={2} display="inline-block">
            <Typography variant="h6" display="inline">
              <b>
                Available: <span className={classes.greyText}>{getAvailableCreationsLabel()}</span>
              </b>
            </Typography>
          </Box>

          <Typography variant="h6" display="inline">
            <b>
              Total: <span className={classes.greyText}>{allCreations.length}</span>
            </b>
          </Typography> */}

          {user.tp_user === UserTypeEnum.TRIAL && (
            <Box display="inline-block">
              <ResearcherLifeCouponModal
                initialModalState={shouldOpenActivationModal}
                setValidCoupon={setValidCoupon}
                purpleBallonDesign={false}
                fromHeader={false}
              />
            </Box>
          )}
        </Grid>
        <Grid className={classes.textRight} item={true} xs={2}>
          <Tooltip classes={{ tooltip: classes.tooltip }} title="Create new folder" placement="top">
            <IconButton onClick={onCreateFolderClick} size="large">
              <CreateNewFolderOutlined fontSize="large" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      <Divider className={classes.divider} />
    </>
  );
}

Filters.propTypes = Props;
